import { reactive } from 'vue';

/**
 * Lets a form's primary action bar — a Modal footer or a full-page view's own
 * sticky Save/Cancel row — reserve viewport space so LumiShell's *floating*
 * presentation (its expanded window, anchored bottom-right) shifts clear of it instead of visually overlapping it (make
 * brief ai-switch-notify-launcher, outcome invariant 8). Dock mode is
 * untouched: it already reflows the content area rather than floating over
 * it, so it never needed this.
 *
 * Why this exists despite the overlay z-index ladder (tokens/layers.css)
 * already putting a modal (120) above the floating window (--layer-popover,
 * 60): `Modal.vue`'s backdrop is translucent + blurred, not opaque, so
 * whatever sits behind it at a lower z-index still shows through, dimmed —
 * including that window, right where a wide modal's own footer buttons often
 * land (bottom-right). Z-index alone settles *who wins a literal overlap*;
 * it does not stop the two from visually reading as on top of each other in
 * the same corner. This reserves that corner outright instead.
 *
 * Multiple action bars can be registered at once (e.g. a form host inside
 * a page that also opens a confirm-flavoured modal on top): the reserved
 * amount is always the tallest currently-registered contributor, so retiring
 * an inner one correctly falls back to an outer one's height rather than to
 * zero while the outer one is still showing.
 */
const contributions = new Map<symbol, number>();

export const launcherAvoidance = reactive({ reservedBottomPx: 0 });

function recompute() {
  let max = 0;
  for (const px of contributions.values()) if (px > max) max = px;
  launcherAvoidance.reservedBottomPx = max;
}

/**
 * A form action bar's typical footprint (button height + surrounding
 * padding) plus a small clearance above it. Producers pass this rather than
 * measuring their own DOM — the reserved band only needs to be at least as
 * tall as the real bar, not pixel-exact, since the goal is "clearly not
 * overlapping," not flush alignment.
 */
export const FORM_ACTION_BAR_CLEARANCE_PX = 96;

/** Call when a form's action bar becomes visible (Modal opening, or the form
 *  itself mounting for an always-open one like a full-page tab). Returns a
 *  token for `releaseFormActionBar` — hold onto it, one registration per
 *  call, never reused. */
export function reserveFormActionBar(heightPx: number = FORM_ACTION_BAR_CLEARANCE_PX): symbol {
  const token = Symbol('form-action-bar');
  contributions.set(token, heightPx);
  recompute();
  return token;
}

/** Call when that action bar is no longer visible (Modal closing/unmounting,
 *  or the form itself unmounting). A `null` token is a no-op, so callers can
 *  release unconditionally without tracking whether they ever reserved. */
export function releaseFormActionBar(token: symbol | null): void {
  if (token === null) return;
  contributions.delete(token);
  recompute();
}
